import React from 'react';
import _ from 'lodash';
import { FormControlLabel, Typography, Stack, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation } from 'react-fetching-library';
import { useDispatch, useSelector } from 'react-redux';

import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { submitModuleViewAction, updateEmployeeConfigAction } from '../../../services/request';
import {
  setOLOrderFilters,
  setZSOrderFilters,
  setPLOrderFilters,
  setWROrderFilters,
  setOLOrderHiddenColumns,
  setZSOrderHiddenColumns,
  setPLOrderHiddenColumns,
  setWROrderHiddenColumns,
  setOLOrderColumnsWidth,
  setZSOrderColumnsWidth,
  setPLOrderColumnsWidth,
  setWROrderColumnsWidth,
  setOLOrderColumnsSequence,
  setZSOrderColumnsSequence,
  setPLOrderColumnsSequence,
  setWROrderColumnsSequence,
  setOLOrderSearchFields,
  setZSOrderSearchFields,
  setPLOrderSearchFields,
  setWROrderSearchFields
} from '../../../services/redux-actions';

export const ViewList = props => {
  const { data, deleteViewCallback, getViewDataCallback } = props;
  const dispatch = useDispatch();
  const { employeeId, employeeConfigId, module } = useSelector(state => state.app);
  const { loading, mutate } = useMutation(submitModuleViewAction);
  const { loading: configLoading, mutate: configMutate } = useMutation(updateEmployeeConfigAction);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [idToDelete, setIdToDelete] = React.useState(null);

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', idToDelete);
    if (!error) {
      deleteViewCallback(idToDelete);
      setConfirmDeleteOpen(false);
    }
  };

  const setModuleFilter = viewData => {
    if (module === 'obozy') {
      return setOLOrderFilters(viewData.ol_order_filters);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderFilters(viewData.zs_order_filters);
    } else if (module === 'polkolonie') {
      return setPLOrderFilters(viewData.pl_order_filters);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderFilters(viewData.wr_order_filters);
    }
  };

  const setModuleHiddenColumns = viewData => {
    if (module === 'obozy') {
      return setOLOrderHiddenColumns(viewData.ol_order_hidden_columns);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderHiddenColumns(viewData.zs_order_hidden_columns);
    } else if (module === 'polkolonie') {
      return setPLOrderHiddenColumns(viewData.pl_order_hidden_columns);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderHiddenColumns(viewData.wr_order_hidden_columns);
    }
  };

  const setModuleColumnsWidth = viewData => {
    if (module === 'obozy') {
      return setOLOrderColumnsWidth(viewData.ol_order_columns_width);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderColumnsWidth(viewData.zs_order_columns_width);
    } else if (module === 'polkolonie') {
      return setPLOrderColumnsWidth(viewData.pl_order_columns_width);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderColumnsWidth(viewData.wr_order_columns_width);
    }
  };

  const setModuleColumnsSequence = viewData => {
    if (module === 'obozy') {
      return setOLOrderColumnsSequence(viewData.ol_order_columns_sequence);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderColumnsSequence(viewData.zs_order_columns_sequence);
    } else if (module === 'polkolonie') {
      return setPLOrderColumnsSequence(viewData.pl_order_columns_sequence);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderColumnsSequence(viewData.wr_order_columns_sequence);
    }
  };

  const setModuleSearchFields = viewData => {
    if (module === 'obozy') {
      return setOLOrderSearchFields(viewData.ol_order_search_fields);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderSearchFields(viewData.zs_order_search_fields);
    } else if (module === 'polkolonie') {
      return setPLOrderSearchFields(viewData.pl_order_search_fields);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderSearchFields(viewData.wr_order_search_fields);
    }
  };

  const constructMutatationBody = (filtersPayload, hiddenColumnsPayload, columnsWidthPayload, columnsSequencePayload, searchFieldsPayload) => {
    const data = {};
    data.employee = employeeId;
    if (module === 'obozy') {
      data.ol_order_filters = filtersPayload;
      data.ol_order_hidden_columns = hiddenColumnsPayload;
      data.ol_order_columns_width = columnsWidthPayload;
      data.ol_order_columns_sequence = columnsSequencePayload;
      data.ol_order_search_fields = searchFieldsPayload;
    } else if (module === 'zielone_szkoly') {
      data.zs_order_filters = filtersPayload;
      data.zs_order_hidden_columns = hiddenColumnsPayload;
      data.zs_order_columns_width = columnsWidthPayload;
      data.zs_order_columns_sequence = columnsSequencePayload;
      data.zs_order_search_fields = searchFieldsPayload;
    } else if (module === 'polkolonie') {
      data.pl_order_filters = filtersPayload;
      data.pl_order_hidden_columns = hiddenColumnsPayload;
      data.pl_order_columns_width = columnsWidthPayload;
      data.pl_order_columns_sequence = columnsSequencePayload;
      data.pl_order_search_fields = searchFieldsPayload;
    } else if (module === 'wyjazdy_rodzinne') {
      data.wr_order_filters = filtersPayload;
      data.wr_order_hidden_columns = hiddenColumnsPayload;
      data.wr_order_columns_width = columnsWidthPayload;
      data.wr_order_columns_sequence = columnsSequencePayload;
      data.wr_order_search_fields = searchFieldsPayload;
    }

    return data;
  };

  const handleViewChange = async id => {
    const viewData = getViewDataCallback(id);
    const { payload: filtersPayload } = dispatch(setModuleFilter(viewData));
    const { payload: hiddenColumnsPayload } = dispatch(setModuleHiddenColumns(viewData));
    const { payload: columnsWidthPayload } = dispatch(setModuleColumnsWidth(viewData));
    const { payload: columnsSequencePayload } = dispatch(setModuleColumnsSequence(viewData));
    const { payload: searchFieldsPayload } = dispatch(setModuleSearchFields(viewData));
    const { error } = await configMutate(
      constructMutatationBody(
        filtersPayload, hiddenColumnsPayload, columnsWidthPayload, columnsSequencePayload, searchFieldsPayload
      ), employeeConfigId);
  };

  return (
    <TreeView
      sx={{ mt: 2 }}
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpanded={['all']}
      defaultExpandIcon={<ChevronRightIcon />}
      defaultEndIcon={null}
    >
      <TreeItem nodeId="1" label={<Typography sx={{ padding: 1 }}>Widoki</Typography>}>
        {_.map(data, (obj, key) => <TreeItem key={key} nodeId={(key + 2).toString()} onClick={() => handleViewChange(obj.id)}
          label={
            <FormControlLabel value={obj.id} control={
              <Tooltip title="Wczytaj widok" sx={{ pr: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: 240, pt: 1, pb: 1 }}>
                  {obj.name}
                  <Tooltip title="Usuń widok...">
                    <DeleteIcon color="error" sx={{ ml: 'auto', zIndex: 999999 }} onClick={() => {
                      setConfirmDeleteOpen(true);
                      setIdToDelete(obj.id);
                    }} />
                  </Tooltip>
                </Stack>
              </Tooltip>}
            />}
        />
        )}
      </TreeItem>
      <ConfirmDialog
        title="Usunąć Widok?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć ten widok?
      </ConfirmDialog>
    </TreeView>
  );
};
